// src/prototype/components/sep/SEPReportView.tsx
// ============================================================
// SEP Report View — orchestrator
// Fetches /api/v2/strategic-execution-plan and renders all 10 sections
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FileBarChart2, RotateCcw } from 'lucide-react';
import { SEPReportIndex } from './SEPReportIndex';
import { SEPSectionExecutiveSummary } from './SEPSectionExecutiveSummary';
import { SEPSectionExecutionStrategy } from './SEPSectionExecutionStrategy';
import { SEPSectionPortfolioImpact } from './SEPSectionPortfolioImpact';
import { SEPSectionInitiativePortfolio } from './SEPSectionInitiativePortfolio';
import { SEPSectionExecutionSequencing } from './SEPSectionExecutionSequencing';
import { SEPSectionResourcePlan } from './SEPSectionResourcePlan';
import { SEPSectionDependenciesRisk } from './SEPSectionDependenciesRisk';
import { SEPSectionGovernance } from './SEPSectionGovernance';
import { SEPSectionImplementationRoadmap } from './SEPSectionImplementationRoadmap';
import { SEPSectionExecutionRecommendation } from './SEPSectionExecutionRecommendation';
import {
  fetchStrategicExecutionPlan,
  StrategicExecutionPlanError,
  type StrategicExecutionPlanResponse,
} from '../../services/strategicExecutionPlanService';
import { mapStrategicExecutionPlan } from '../../utils/mapStrategicExecutionPlan';

export interface SEPFinalist {
  id: string;
  rank: number;
  title: string;
  category: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit: string };
  validationScore: number;
  executionRationale: string;
  scenarios: {
    conservative: { roiLow: number; roiHigh: number; paybackMonths: number };
    base: { roiLow: number; roiHigh: number; paybackMonths: number };
    aggressive: { roiLow: number; roiHigh: number; paybackMonths: number };
  };
}

export interface SEPReportViewProps {
  clientId: string;
  portfolioIds: string[];
}

type LoadStatus = 'idle' | 'loading' | 'success' | 'error' | 'empty';

const SectionAnchor: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <div id={id} style={{ scrollMarginTop: 'var(--sep-sticky-offset, 160px)' }}>{children}</div>
);

const LoadingState: React.FC = () => (
  <div className="flex-1 min-w-0 bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-12 text-center">
    <div className="w-10 h-10 rounded-full border-2 border-[#18365E]/30 border-t-[#18365E] animate-spin mx-auto mb-4" />
    <p className="text-sm font-semibold text-[#18365E]">Loading Strategic Execution Plan…</p>
    <p className="text-xs text-[#717182] mt-1">Fetching portfolio data from the server</p>
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex-1 min-w-0 bg-white rounded-xl border border-rose-200 shadow-sm p-10 text-center">
    <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
      <AlertTriangle className="w-7 h-7 text-rose-500" />
    </div>
    <h3 className="text-lg font-semibold text-[#18365E] mb-2">Unable to load Strategic Execution Plan</h3>
    <p className="text-sm text-[#717182] mb-6 max-w-md mx-auto leading-relaxed">{message}</p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#18365E] text-white rounded-lg font-medium hover:bg-[#18365E]/90 transition-colors"
    >
      <RotateCcw className="w-4 h-4" />Retry
    </button>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex-1 min-w-0 bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-12 text-center">
    <div className="w-14 h-14 rounded-full bg-[#F5F7FA] flex items-center justify-center mx-auto mb-4">
      <FileBarChart2 className="w-7 h-7 text-[#717182]" />
    </div>
    <h3 className="text-lg font-semibold text-[#18365E] mb-2">No execution plan data available</h3>
    <p className="text-sm text-[#717182] max-w-md mx-auto leading-relaxed">
      The Strategic Execution Plan API returned no initiatives for the selected portfolio.
      Return to Evaluate &amp; Shortlist and confirm your finalist selections.
    </p>
  </div>
);

export const SEPReportView: React.FC<SEPReportViewProps> = ({ clientId, portfolioIds }) => {
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [planResponse, setPlanResponse] = useState<StrategicExecutionPlanResponse | null>(null);

  const portfolioKey = useMemo(() => portfolioIds.slice().sort().join(','), [portfolioIds]);

  const loadPlan = useCallback(async (signal?: { cancelled: boolean }) => {
    if (!clientId || portfolioIds.length === 0) {
      setStatus('empty');
      setPlanResponse(null);
      setErrorMessage(null);
      return;
    }

    setStatus('loading');
    setErrorMessage(null);

    try {
      const response = await fetchStrategicExecutionPlan(clientId, portfolioIds);
      if (signal?.cancelled) return;

      const mapped = mapStrategicExecutionPlan(response);
      if (!mapped.hasData) {
        setPlanResponse(response);
        setStatus('empty');
        return;
      }

      setPlanResponse(response);
      setStatus('success');
    } catch (err) {
      if (signal?.cancelled) return;
      const message =
        err instanceof StrategicExecutionPlanError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'An unexpected error occurred while loading the Strategic Execution Plan.';
      setErrorMessage(message);
      setPlanResponse(null);
      setStatus('error');
      console.error('[SEPReportView] Failed to load strategic execution plan:', err);
    }
  }, [clientId, portfolioIds]);

  useEffect(() => {
    const signal = { cancelled: false };
    loadPlan(signal);
    return () => { signal.cancelled = true; };
  }, [loadPlan, portfolioKey]);

  const mapped = useMemo(
    () => (planResponse ? mapStrategicExecutionPlan(planResponse) : null),
    [planResponse]
  );

  const sections = planResponse?.sections;

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex gap-6 items-start">
        <SEPReportIndex />
        <LoadingState />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex gap-6 items-start">
        <SEPReportIndex />
        <ErrorState message={errorMessage ?? 'Something went wrong.'} onRetry={() => loadPlan()} />
      </div>
    );
  }

  if (status === 'empty' || !mapped) {
    return (
      <div className="flex gap-6 items-start">
        <SEPReportIndex />
        <EmptyState />
      </div>
    );
  }

  const {
    clientName,
    finalists,
    initiativesSelectedCount,
    executiveSummaryInitiatives,
    initiativePortfolioItems,
    executionRoadmapInitiatives,
    executionHorizonMonths,
    resourcePlanInitiatives,
    financialSummary,
    governanceFramework,
    risksAndDependencies,
    approvalChecklist,
    portfolioRoiLow,
    portfolioRoiHigh,
    executionHorizon,
    quickWinCount,
  } = mapped;

  return (
    <div className="flex gap-6 items-start">
      <SEPReportIndex />

      <div id="sep-report-export-root" className="relative z-0 flex-1 min-w-0 flex flex-col gap-6">
        <SectionAnchor id="sep-exec-summary">
          <SEPSectionExecutiveSummary
            clientName={clientName}
            finalists={executiveSummaryInitiatives}
            initiativesSelectedCount={initiativesSelectedCount}
            portfolioRoiLow={portfolioRoiLow}
            portfolioRoiHigh={portfolioRoiHigh}
            executionHorizon={executionHorizon}
            quickWinCount={quickWinCount}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-execution-strategy">
          <SEPSectionExecutionStrategy
            clientName={clientName}
            finalists={finalists}
            executionApproach={sections?.execution_approach}
            quickWinCount={quickWinCount}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-portfolio-impact">
          <SEPSectionPortfolioImpact financialSummary={financialSummary} />
        </SectionAnchor>

        <SectionAnchor id="sep-initiative-portfolio">
          <SEPSectionInitiativePortfolio
            initiatives={initiativePortfolioItems}
            clientName={clientName}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-exec-sequencing">
          <SEPSectionExecutionSequencing
            clientName={clientName}
            horizonMonths={executionHorizonMonths}
            initiatives={executionRoadmapInitiatives}
            sequencingInsights={sections?.execution_roadmap?.sequencing_insights}
            quickWinCount={quickWinCount}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-resource-plan">
          <SEPSectionResourcePlan
            clientName={clientName}
            initiatives={resourcePlanInitiatives}
            capacityPlanningNotes={sections?.resource_requirements?.capacity_planning_notes}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-dependencies-risk">
          <SEPSectionDependenciesRisk
            clientName={clientName}
            risksAndDependencies={risksAndDependencies}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-governance">
          <SEPSectionGovernance
            clientName={clientName}
            governanceFramework={governanceFramework}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-impl-roadmap">
          <SEPSectionImplementationRoadmap
            finalists={finalists}
            clientName={clientName}
            portfolioOverview={sections?.portfolio_overview}
          />
        </SectionAnchor>

        <SectionAnchor id="sep-recommendation">
          <SEPSectionExecutionRecommendation
            clientName={clientName}
            approvalChecklist={approvalChecklist}
          />
        </SectionAnchor>
      </div>
    </div>
  );
};

export default SEPReportView;
